import React, { createRef, useCallback, useEffect, useState } from "react";
import { StyleSheet, Text, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { Header, NavigationBar, TableRow, AverageTimeRow } from "./components";
import {
  Badge,
  Button,
  UserCard,
  ReviewCarousel,
  RelatedOffersList,
  ActionDialog,
} from "src/components";
import { Rating, Likes } from "src/components/offer-list-item-large/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import { stringDateFormat } from "src/utils/date";
import { useAuth, AuthState } from "src/hooks/cognito";
import {
  useLikeActions,
  useBlockActions,
  useUser,
  useOffer,
  useReviewsByOffer,
  useReviewsByOfferToUser,
  useRelatedOffers,
  useCognito,
  usePreOfferByService,
  useAccount,
} from "src/hooks";
import { IServiceFilters } from "src/domains";
import LocalText from "src/local";
import { RootStackScreenProps } from "src/navigation/types";

export type OfferNavigationParam = {
  offerId: string;
};

const Index: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"Offer">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"Offer">["route"]>();
  const [moreDialogOpen, setMoreDialogOpen] = useState<boolean>(false);
  const auth = useAuth();
  const [cognitoUsername] = useCognito();
  const [offer] = useOffer(route.params.offerId, true);
  const [owner] = useUser(offer?.owner?.id);
  const [account] = useAccount();
  const [reviewByOfferItems] = useReviewsByOffer(route.params.offerId);
  const [relatedOffersFilter, setRelatedOffersFilter] = useState<
    IServiceFilters | undefined
  >();
  // const [reviewByOfferToUserItems] = useReviewsByOfferToUser(
  //   route.params.offerId,
  // );
  const [relatedOffers] = useRelatedOffers(relatedOffersFilter);
  const [preOfferConversation, preOfferConversationLoading] =
    usePreOfferByService(route.params.offerId);
  const { like, dislike } = useLikeActions();
  const { block } = useBlockActions();

  useEffect(() => {
    setRelatedOffersFilter(
      offer?.category?.id
        ? {
            categories: [offer?.category?.id],
            excludeIds: [route.params.offerId],
            isBlocked: false,
            isDisabled: false,
          }
        : undefined
    );
  }, [offer]);

  const moreActionSheetHandler = async (index: number) => {
    if (index === 0) {
      if (offer?.owner?.id) {
        await block(offer?.owner?.id);
        navigation.goBack();
      }
    }
  };

  const likeToggle = async () => {
    if (offer?.isLiked) {
      await dislike(route.params.offerId);
    } else {
      await like(route.params.offerId);
    }
  };

  const preOfferHandler = useCallback(() => {
    if (auth === AuthState.Auth) {
      if (preOfferConversation) {
        navigation.push("PreOfferChat", {
          preOfferId: preOfferConversation.id,
        });
      } else {
        navigation.push("PreOfferChat", {
          offerId: route.params.offerId,
          isOwner: true,
        });
      }
    } else {
      window.open(
        `/auth?redirect=%2Fservices%2F${route.params.offerId}`,
        "_blank"
      );
    }
  }, [route, navigation, preOfferConversation, auth]);

  const createOfferHandler = useCallback(() => {
    if (auth === AuthState.Auth) {
      navigation.push("CallRequest", { offerId: offer?.id || "-1" });
    } else {
      window.open(
        `/auth?redirect=%2Fservices%2F${route.params.offerId}`,
        "_blank"
      );
    }
  }, [route, navigation, offer, auth]);

  return (
    <View style={styles.container}>
      <NavigationBar
        isAuth={auth === AuthState.Auth}
        isOwn={cognitoUsername === offer?.owner?.id}
        isFavorite={offer?.isLiked}
        onBack={() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
          } else {
            navigation.navigate("Main", { screen: "Search" });
          }
        }}
        onFavorite={likeToggle}
        onMore={() => setMoreDialogOpen(true)}
        onEdit={() =>
          navigation.push("OfferEdit", { offerId: route.params.offerId })
        }
      />
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        <Header
          status={offer?.owner?.status}
          images={offer?.images?.map((image) => ({ uri: image })) || []}
        />
        {offer ? (
          <>
            <Text style={styles.title}>{offer?.title}</Text>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: 8,
                paddingBottom: 4,
                paddingHorizontal: 16,
              }}
            >
              <Text style={styles.price}>
                ¥<Text style={styles.priceBold}>{offer?.price}</Text> /{" "}
                <Text style={styles.priceBold}>1</Text>分
              </Text>
              <Text style={styles.createdAt}>
                制作日：
                {offer?.createdAt ? stringDateFormat(offer?.createdAt) : "未定"}
              </Text>
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                paddingTop: 4,
                paddingBottom: 8,
                paddingHorizontal: 16,
                justifyContent: "space-between",
              }}
            >
              <Badge
                text={offer?.category?.title || "unknown"}
                color={colors.chokuGray5}
              />
              <Text style={styles.createdAt}>
                最終更新日：
                {offer?.updatedAt ? stringDateFormat(offer?.updatedAt) : "未定"}
              </Text>
            </View>
            <View style={styles.row}>
              <Rating value={offer?.rate || 0} count={offer?.rateCount || 0} />
              <Likes value={offer?.likeCount || 0} />
            </View>
            <TableRow
              containerStyle={styles.tableRow1}
              text={LocalText.screens.offer.usageCountText}
              detail={`${offer?.usages || 0}`}
              sufix={LocalText.screens.offer.usageCountSufix}
            />
            <AverageTimeRow
              containerStyle={styles.tableRow2}
              averageTime={offer.averageTime}
            />
            {owner ? (
              <UserCard
                contanerStyle={styles.owner}
                source={owner?.image ? { uri: owner?.image } : undefined}
                status={offer?.owner?.status}
                name={owner?.name}
                birthday={owner?.birthday}
                gender={owner?.gender}
                prefecture={owner?.address?.prefecture}
                isVerified={owner?.identificationStatus === "APPROVED"}
                lastLoginAt={owner.lastLoginAt}
                onPress={() => navigation.push("Owner", { id: owner.id })}
              />
            ) : null}
            <Text style={styles.description}>{offer?.description}</Text>
          </>
        ) : null}
        {reviewByOfferItems.length > 0 ? (
          <ReviewCarousel
            title={LocalText.screens.offer.review}
            reviews={reviewByOfferItems}
            onMore={() =>
              navigation.push("ReviewByOffer", { id: offer?.id || "-1" })
            }
          />
        ) : null}
        {/* {reviewByOfferToUserItems.length > 0 ? (
          <ReviewCarousel
            title={'販売者レビュー'}
            reviews={reviewByOfferToUserItems}
            onMore={() =>
              navigation.push('ReviewByOfferToUser', {id: offer?.id || '-1'})
            }
          />
        ) : null} */}
        {/* {relatedOffers.length > 0 ? (
          <RelatedOffersList
            offers={relatedOffers}
            onOffer={o => navigation.push('Offer', {offerId: o.id})}
          />
        ) : null} */}
      </ScrollView>
      {owner && cognitoUsername !== offer?.owner?.id && !account?.isDisabled ? (
        <View style={styles.actionContainer}>
          <Button
            containerStyle={styles.chatActionContainer}
            variant={"secondary"}
            icon={"chat"}
            iconColor={colors.chokuRed}
            title={LocalText.screens.offer.openPreOfferChat}
            disabled={preOfferConversationLoading}
            onPress={preOfferHandler}
          />
          <View style={styles.offerActionContainer}>
            <Button
              variant={"primary"}
              icon={"send"}
              title={LocalText.screens.offer.openOffer}
              onPress={createOfferHandler}
            />
          </View>
        </View>
      ) : null}
      <ActionDialog
        open={moreDialogOpen}
        onClose={() => setMoreDialogOpen(false)}
        onAction={(index) => {
          setMoreDialogOpen(false);
          moreActionSheetHandler(index);
        }}
        items={[LocalText.screens.offer.block].map((key) => ({
          key,
          label: key,
        }))}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    paddingHorizontal: 16,
    paddingTop: 16,
    lineHeight: 24,
  },
  createdAt: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.chokuGray5,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  tableRow1: {
    marginTop: 8,
  },
  tableRow2: {
    marginTop: 1,
  },
  description: {
    fontSize: 12,
    lineHeight: 18,
    color: "#333333",
    paddingTop: 16,
    paddingHorizontal: 16,
  },
  owner: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  actionContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 16,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    backgroundColor: colors.white,
  },
  chatActionContainer: {
    flex: 1,
    marginRight: 8,
  },
  offerActionContainer: {
    flex: 1,
    marginLeft: 8,
  },
  price: {
    textAlign: "center",
    fontSize: 12,
    color: colors.chokuRed,
  },
  priceBold: {
    fontSize: 14,
    fontWeight: "bold",
  },
});

export default Index;
